// src/routes/JobPostCreation/InlineAnalysisBadge.js

import React, { useState } from 'react';
import { Box, Chip, Collapse, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getScoreBorderColor } from './analysisFieldMapper';
import { bg, text as textColors, border, overlay, statusBg, statusBorder } from '../../utils/darkModeAdapter';

const AI_COLOR = '#16A34A';

/**
 * 各フィールド下に表示するインライン分析バッジ
 *
 * @param {Array} analyses - getFieldAnalyses() の結果（ルールベース）
 * @param {Object} aiMap - axis key → { assessment, suggestions } のマップ（AI補足、任意）
 */
function InlineAnalysisBadge({ analyses, aiMap }) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  if (!analyses || analyses.length === 0) return null;

  // AI補足があるデータを収集
  const aiEntries = [];
  if (aiMap) {
    analyses.forEach((a) => {
      const aiData = aiMap[a.key];
      if (aiData && (aiData.assessment || (aiData.suggestions && aiData.suggestions.length > 0))) {
        aiEntries.push({ ...aiData, label: a.label, score: a.score });
      }
    });
  }

  return (
    <Box sx={{ mt: 0.5, mb: 1 }}>
      {/* チップ行 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexWrap: 'wrap',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(prev => !prev)}
      >
        {analyses.map((a) => {
          const color = getScoreBorderColor(a.score);
          const statusIcon = a.score >= 80 ? '\u2713' : a.score >= 60 ? '\u25B3' : '\uFF01';
          return (
            <Chip
              key={a.key}
              label={`${statusIcon} ${a.label} ${a.score}`}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.72rem',
                fontWeight: 600,
                backgroundColor: `${color}12`,
                color: color,
                border: `1px solid ${color}40`,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          );
        })}

        {/* 展開トグル */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: '#999',
            fontSize: '0.7rem',
            ml: 0.5,
          }}
        >
          {expanded ? (
            <ExpandLessIcon sx={{ fontSize: 16 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          )}
        </Box>
      </Box>

      {/* 詳細展開 */}
      <Collapse in={expanded}>
        {/* ルールベース分析 */}
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            borderRadius: 1.5,
            backgroundColor: overlay(theme).lightest,
            border: `1px solid ${border(theme).subtle}`,
          }}
        >
          {analyses.map((a, idx) => (
            <Box key={a.key} sx={{ mb: idx < analyses.length - 1 ? 1.5 : 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: getScoreBorderColor(a.score),
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: textColors(theme).secondary, fontSize: '0.75rem' }}
                >
                  {a.label}（{a.score}/100）
                </Typography>
              </Box>

              {/* ルールベース: message */}
              {a.data?.message && (
                <Typography
                  variant="caption"
                  sx={{ color: textColors(theme).muted, display: 'block', pl: 1.5, lineHeight: 1.4, fontSize: '0.72rem' }}
                >
                  {a.data.message}
                </Typography>
              )}

              {/* ルールベース: recommendation */}
              {a.data?.recommendation && (
                <Typography
                  variant="caption"
                  sx={{
                    color: getScoreBorderColor(a.score),
                    display: 'block',
                    pl: 1.5,
                    mt: 0.3,
                    lineHeight: 1.4,
                    fontSize: '0.72rem',
                    fontWeight: 500,
                  }}
                >
                  → {a.data.recommendation}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        {/* AI人事アドバイス（独立カード） */}
        {aiEntries.length > 0 && (
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: statusBg(theme).success,
              border: `1px solid ${statusBorder(theme).success}`,
            }}
          >
            {/* AI人事ヘッダー */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <AutoAwesomeIcon sx={{ fontSize: 14, color: AI_COLOR }} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: AI_COLOR, fontSize: '0.76rem' }}
              >
                AI人事のアドバイス
              </Typography>
            </Box>

            {aiEntries.map((entry, idx) => (
              <Box key={idx} sx={{ mb: idx < aiEntries.length - 1 ? 1.5 : 0 }}>
                {/* 軸ラベル */}
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: textColors(theme).primary,
                    fontSize: '0.74rem',
                    display: 'block',
                    mb: 0.3,
                  }}
                >
                  {entry.label}
                </Typography>

                {/* AI評価コメント */}
                {entry.assessment && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: textColors(theme).secondary,
                      display: 'block',
                      lineHeight: 1.5,
                      fontSize: '0.73rem',
                      mb: 0.5,
                    }}
                  >
                    {entry.assessment}
                  </Typography>
                )}

                {/* AI改善提案 */}
                {entry.suggestions && entry.suggestions.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                    {entry.suggestions.map((sug, si) => (
                      <Box key={si} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: AI_COLOR,
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            lineHeight: 1.5,
                            flexShrink: 0,
                          }}
                        >
                          →
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: textColors(theme).primary,
                            fontSize: '0.72rem',
                            fontWeight: 500,
                            lineHeight: 1.5,
                          }}
                        >
                          {sug}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

export default InlineAnalysisBadge;
